import { hash } from './background.js'
import { crunch, missingTexture, shiftRows, sortColumns } from './glitch.js'
import { createBitmap, lerp } from './raster.js'
import { XorShift128 } from './rng.js'

// A top-down Minecraft world for the challenges page, drawn like an in-game
// map item: one pixel per block in the map palette, shaded by whether a block
// is higher or lower than the one north of it, water dithered by depth. Every
// category gets a biome of its own with rivers along the borders, and the
// world is broken the way the 2027 poster is: crunched chunks, chunks that
// never loaded, torn rows and the Far Lands along the east edge.

export const CHUNK = 16

const SEA = 62

// What a block is, besides its biome.
export const LAND = 0
export const OCEAN = 1
export const RIVER = 2
export const FAR_LANDS = 3
export const MISSING = 4

// MapColor values from the game.
const GRASS = [127, 178, 56]
const SAND = [247, 233, 163]
const WOOL = [199, 199, 199]
const ICE = [160, 160, 255]
const PLANT = [0, 124, 0]
const SNOW = [255, 255, 255]
const CLAY = [164, 168, 184]
const DIRT = [151, 109, 77]
const STONE = [112, 112, 112]
const WATER = [64, 64, 255]
const WOOD = [143, 119, 72]
const ORANGE = [216, 127, 51]
const MAGENTA = [178, 76, 216]
const LIGHT_BLUE = [102, 153, 216]
const YELLOW = [229, 229, 51]
const LIGHT_GREEN = [127, 204, 25]
const PINK = [242, 127, 165]
const CYAN = [76, 127, 153]
const PURPLE = [127, 63, 178]
const BROWN = [102, 76, 51]
const GREEN = [102, 127, 51]
const RED = [153, 51, 51]
const BLACK = [25, 25, 25]
const GOLD = [250, 238, 77]
const PODZOL = [129, 86, 49]
const NETHER = [112, 2, 0]
const TERRACOTTA_WHITE = [209, 177, 161]
const TERRACOTTA_ORANGE = [159, 82, 36]
const TERRACOTTA_YELLOW = [186, 133, 36]
const TERRACOTTA_RED = [142, 60, 46]
const TERRACOTTA_BROWN = [76, 50, 35]
const CRIMSON_NYLIUM = [189, 48, 49]
const WARPED_NYLIUM = [22, 126, 134]
const WARPED_WART = [20, 180, 133]
const DEEPSLATE = [100, 100, 100]
const LAVA = [216, 92, 20] // FIRE reads as a hole in the map, so a little warmer

// Brightness of a map pixel: lower than the block to the north, level, higher.
const LOW = 180 / 255
const NORMAL = 220 / 255
const HIGH = 1

function smooth (t) {
    return t * t * (3 - 2 * t)
}

function valueNoise (x, y, seed) {
    const x0 = Math.floor(x)
    const y0 = Math.floor(y)
    const fx = smooth(x - x0)
    const fy = smooth(y - y0)
    const top = lerp(hash(x0, y0, seed), hash(x0 + 1, y0, seed), fx)
    const bottom = lerp(hash(x0, y0 + 1, seed), hash(x0 + 1, y0 + 1, seed), fx)
    return lerp(top, bottom, fy)
}

function fbm (x, y, seed, octaves = 4) {
    let sum = 0
    let norm = 0
    let amp = 0.5
    let f = 1
    for (let i = 0; i < octaves; i++) {
        sum += amp * valueNoise(x * f, y * f, seed + i * 101)
        norm += amp
        amp *= 0.5
        f *= 2
    }
    return sum / norm
}

// Every biome paints a block from its position, the shared elevation `e`,
// two more noise fields and a per-block random number. It returns the map
// colour and how many blocks the surface sticks up (trees, spikes) or a
// negative number for a flat liquid surface that many blocks deep.
const BIOMES = {
    flower_forest: {
        id: 'minecraft:flower_forest',
        paint (b) {
            if (b.r < 0.11) return b.set(PLANT, 2)
            if (b.r < 0.17) return b.set([YELLOW, RED, MAGENTA, LIGHT_BLUE, SNOW][(b.r * 1000) % 5 | 0], 0)
            return b.set(GRASS, 0)
        },
    },
    plains: {
        id: 'minecraft:plains',
        village: true,
        paint (b) {
            if (b.r < 0.018) return b.set(PLANT, 2)
            if (b.n2 > 0.72 && b.r < 0.3) return b.set(LIGHT_GREEN, 0)
            return b.set(GRASS, 0)
        },
    },
    snowy: {
        id: 'minecraft:ice_spikes',
        paint (b) {
            if (b.n2 > 0.66) return b.set(ICE, -1)
            if (b.r < 0.025) return b.set(ICE, 5)
            if (b.r < 0.05) return b.set(GREEN, 2)
            return b.set(SNOW, 0)
        },
    },
    nether: {
        id: 'minecraft:nether_wastes',
        paint (b) {
            if (b.n2 > 0.64) return b.set(LAVA, -3)
            if (b.n3 > 0.62) return b.set(CRIMSON_NYLIUM, 0)
            if (b.r < 0.012) return b.set(GOLD, 1)
            if (b.r < 0.05) return b.set(BROWN, 0)
            return b.set(NETHER, 0)
        },
    },
    desert: {
        id: 'minecraft:desert',
        temple: true,
        paint (b) {
            if (b.r < 0.008) return b.set(PLANT, 2)
            if (b.r < 0.014) return b.set(WOOD, 0)
            if (b.n3 > 0.7) return b.set(TERRACOTTA_WHITE, 1)
            return b.set(SAND, 0)
        },
    },
    swamp: {
        id: 'minecraft:swamp',
        paint (b) {
            if (b.n2 > 0.56) return b.r < 0.06 ? b.set(PLANT, 0) : b.set(WATER, -1)
            if (b.r < 0.08) return b.set(PLANT, 3)
            return b.set(GREEN, 0)
        },
    },
    mushroom: {
        id: 'minecraft:mushroom_fields',
        mushrooms: true,
        paint (b) {
            if (b.r < 0.01) return b.set(RED, 1)
            if (b.r < 0.02) return b.set(BROWN, 1)
            return b.set(PURPLE, 0)
        },
    },
    mountains: {
        id: 'minecraft:jagged_peaks',
        relief: 3,
        paint (b) {
            if (b.e > 0.6) return b.set(SNOW, 0)
            if (b.n3 > 0.68) return b.set(CLAY, 0)
            if (b.e < 0.42 && b.r < 0.08) return b.set(GREEN, 2)
            return b.set(STONE, 0)
        },
    },
    badlands: {
        id: 'minecraft:eroded_badlands',
        relief: 2,
        paint (b) {
            const bands = [TERRACOTTA_ORANGE, TERRACOTTA_YELLOW, TERRACOTTA_WHITE, TERRACOTTA_RED, ORANGE, TERRACOTTA_BROWN]
            if (b.e < 0.4) return b.set(ORANGE, 0)
            return b.set(bands[b.level % bands.length], 0)
        },
    },
    cherry: {
        id: 'minecraft:cherry_grove',
        paint (b) {
            if (b.r < 0.13) return b.set(PINK, 3)
            if (b.r < 0.16) return b.set(WOOD, 0)
            return b.set(GRASS, 0)
        },
    },
    jungle: {
        id: 'minecraft:jungle',
        paint (b) {
            if (b.r < 0.3) return b.set(PLANT, 2 + (b.r * 40 | 0) % 3)
            if (b.r < 0.32) return b.set(LIGHT_GREEN, 1)
            return b.set(GRASS, 0)
        },
    },
    warped: {
        id: 'minecraft:warped_forest',
        paint (b) {
            if (b.n2 > 0.66) return b.set(NETHER, 0)
            if (b.r < 0.1) return b.set(WARPED_WART, 3)
            return b.set(WARPED_NYLIUM, 0)
        },
    },
    taiga: {
        id: 'minecraft:old_growth_spruce_taiga',
        paint (b) {
            if (b.r < 0.3) return b.set(GREEN, 2 + (b.r * 50 | 0) % 2)
            if (b.n2 > 0.7) return b.set(STONE, 0)
            return b.set(PODZOL, 0)
        },
    },
    deep_dark: {
        id: 'minecraft:deep_dark',
        paint (b) {
            if (b.r < 0.015) return b.set(CYAN, 1)
            if (b.n2 > 0.6) return b.set(BLACK, 0)
            return b.set(DEEPSLATE, 0)
        },
    },
    end: {
        id: 'minecraft:end_highlands',
        paint (b) {
            if (b.r < 0.04) return b.set(PURPLE, 3)
            if (b.r < 0.045) return b.set(BLACK, 6)
            return b.set(SAND, 0)
        },
    },
    savanna: {
        id: 'minecraft:savanna',
        paint (b) {
            if (b.r < 0.03) return b.set(PLANT, 2)
            if (b.r < 0.05) return b.set(ORANGE, 1)
            return b.set([189, 178, 95], 0)
        },
    },
}

// Which biome a category gets, by name. First match wins, so the narrower
// patterns go first. Categories nothing matches take the next unused biome.
/* eslint-disable array-element-newline */
const MATCHERS = [
    [/intro|begin|easy|warm|welcome|sanity|nyb/i, 'flower_forest'],
    [/block|chain|web3|smart|eth/i, 'cherry'],
    [/web/i, 'plains'],
    [/crypt/i, 'snowy'],
    [/pwn|bin|exploit|bof/i, 'nether'],
    [/rev/i, 'desert'],
    [/forens|dfir/i, 'swamp'],
    [/misc/i, 'mushroom'],
    [/osint|recon/i, 'mountains'],
    [/hard|hw|iot|radio|embed/i, 'badlands'],
    [/prog|ppc|cod|algo|scripting/i, 'jungle'],
    [/steg/i, 'warped'],
    [/ai|ml|llm|prompt/i, 'deep_dark'],
]
/* eslint-enable array-element-newline */
const FALLBACK = ['taiga', 'warped', 'savanna', 'cherry', 'jungle', 'badlands', 'end', 'deep_dark', 'mountains', 'mushroom', 'desert', 'swamp', 'snowy', 'nether', 'plains', 'flower_forest']

export function biomesFor (names) {
    const used = new Set()
    return names.map(name => {
        const match = MATCHERS.find(([re, biome]) => re.test(name) && !used.has(biome))
        const biome = match ? match[1] : FALLBACK.find(b => !used.has(b)) || FALLBACK[used.size % FALLBACK.length]
        used.add(biome)
        return biome
    })
}

// Pulls a map colour towards the poster: a little desaturated, darker and
// cooler, so the markers on top are what stands out.
function tone (c, k, out, i) {
    const r = c[0] * k
    const g = c[1] * k
    const b = c[2] * k
    const l = 0.299 * r + 0.587 * g + 0.114 * b
    out[i] = lerp(r, l, 0.15) * 0.64 + 10
    out[i + 1] = lerp(g, l, 0.15) * 0.6 + 4
    out[i + 2] = lerp(b, l, 0.15) * 0.66 + 24
    out[i + 3] = 255
}

// Breadth-first distance (in 8-connected steps) from every block in `inside`
// to the nearest block outside it.
function distanceInside (w, h, inside) {
    const dist = new Int16Array(w * h).fill(-1)
    const queue = new Int32Array(w * h)
    let head = 0
    let tail = 0
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x
            if (!inside(i)) continue
            const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1 ||
                !inside(i - 1) || !inside(i + 1) || !inside(i - w) || !inside(i + w)
            if (edge) {
                dist[i] = 1
                queue[tail++] = i
            }
        }
    }
    while (head < tail) {
        const i = queue[head++]
        const x = i % w
        const y = (i / w) | 0
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const nx = x + dx
                const ny = y + dy
                if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue
                const j = ny * w + nx
                if (dist[j] !== -1 || !inside(j)) continue
                dist[j] = dist[i] + 1
                queue[tail++] = j
            }
        }
    }
    return dist
}

// The connected pieces of the blocks where `inside` holds, largest first.
function components (w, h, inside) {
    const seen = new Uint8Array(w * h)
    const out = []
    const stack = []
    for (let start = 0; start < w * h; start++) {
        if (seen[start] || !inside(start)) continue
        const blocks = []
        seen[start] = 1
        stack.push(start)
        while (stack.length) {
            const i = stack.pop()
            blocks.push(i)
            const x = i % w
            const neighbours = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, i - w, i + w]
            for (const j of neighbours) {
                if (j < 0 || j >= w * h || seen[j] || !inside(j)) continue
                seen[j] = 1
                stack.push(j)
            }
        }
        out.push(blocks)
    }
    return out.sort((a, b) => b.length - a.length)
}

// Splits the land between the regions: a power diagram whose cells are
// relaxed towards their centroids and grown or shrunk towards their share
// of the land, then warped with noise so the borders wander.
function assignRegions (w, h, kind, shares, seed, rng) {
    const k = shares.length
    const region = new Int16Array(w * h).fill(-1)
    if (!k) return region
    const land = []
    let minX = w
    let maxX = 0
    let minY = h
    let maxY = 0
    for (let i = 0; i < w * h; i++) {
        if (kind[i] !== LAND) continue
        land.push(i)
        const x = i % w
        const y = (i / w) | 0
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
    }
    const total = shares.reduce((a, b) => a + b, 0)
    const target = shares.map(s => (land.length * s) / total)

    // Start on a grid over the land in reading order, so the first
    // categories end up in the north-west.
    const cols = Math.max(1, Math.round(Math.sqrt((k * (maxX - minX + 1)) / (maxY - minY + 1))))
    const rows = Math.ceil(k / cols)
    const sx = new Float64Array(k)
    const sy = new Float64Array(k)
    const weight = new Float64Array(k)
    for (let i = 0; i < k; i++) {
        const row = (i / cols) | 0
        const inRow = row === rows - 1 ? k - row * cols : cols
        sx[i] = minX + ((i % cols) + 0.5 + rng.range(-0.15, 0.15)) * ((maxX - minX + 1) / inRow)
        sy[i] = minY + (row + 0.5 + rng.range(-0.15, 0.15)) * ((maxY - minY + 1) / rows)
    }

    const nearest = (x, y) => {
        let best = 0
        let bestD = Infinity
        for (let r = 0; r < k; r++) {
            const d = (x - sx[r]) ** 2 + (y - sy[r]) ** 2 - weight[r]
            if (d < bestD) {
                bestD = d
                best = r
            }
        }
        return best
    }

    const area = new Float64Array(k)
    const cx = new Float64Array(k)
    const cy = new Float64Array(k)
    for (let iter = 0; iter < 24; iter++) {
        area.fill(0)
        cx.fill(0)
        cy.fill(0)
        for (const i of land) {
            const x = i % w
            const y = (i / w) | 0
            const r = nearest(x, y)
            area[r]++
            cx[r] += x
            cy[r] += y
        }
        let mean = 0
        for (let r = 0; r < k; r++) {
            if (area[r]) {
                sx[r] = cx[r] / area[r]
                sy[r] = cy[r] / area[r]
            }
            weight[r] += 0.6 * (target[r] - area[r])
            mean += weight[r] / k
        }
        for (let r = 0; r < k; r++) weight[r] -= mean
    }

    for (const i of land) {
        const x = i % w
        const y = (i / w) | 0
        const wx = x + (fbm(x / 13, y / 13, seed + 31, 3) - 0.5) * 16
        const wy = y + (fbm(x / 13, y / 13, seed + 37, 3) - 0.5) * 16
        region[i] = nearest(wx, wy)
    }

    // The warp can leave crumbs of a region inside another; give each
    // region only its largest piece and hand the crumbs to their neighbours.
    for (let r = 0; r < k; r++) {
        const pieces = components(w, h, i => region[i] === r)
        for (const piece of pieces.slice(1)) for (const i of piece) region[i] = -2
    }
    for (let pass = 0; pass < 64; pass++) {
        let left = 0
        for (const i of land) {
            if (region[i] !== -2) continue
            const counts = new Map()
            for (const j of [i - 1, i + 1, i - w, i + w]) {
                if (j < 0 || j >= w * h || region[j] < 0) continue
                counts.set(region[j], (counts.get(region[j]) || 0) + 1)
            }
            if (counts.size) region[i] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
            else left++
        }
        if (!left) break
    }
    return region
}

// Marker placement. Distances are Chebyshev in blocks with horizontal steps
// counted at `ax`, the height of a marker over its width, so two markers stop
// overlapping once they are `clear` (a marker's height) apart. Boxes are
// { x, y, hw, hh }: centre and half size; markers are boxes of no size.
function gapTo (c, b, ax) {
    const dx = Math.max(0, Math.abs(c[0] - b.x) - (b.hw || 0))
    const dy = Math.max(0, Math.abs(c[1] - b.y) - (b.hh || 0))
    return Math.max(dx * ax, dy)
}

// How good a block ([x, y, room]) is for a marker with `space` to the
// nearest other: overlapping is expensive, past a little more than clear more
// space doesn't matter, and being away from rivers and coasts is a bonus.
function placeValue (space, c, clear) {
    const s = Math.min(space, clear * 1.4)
    return (s < clear ? clear - (clear - s) * 4 : s) + 0.4 * Math.min(c[2], 6)
}

// Spreads `count` markers over a region's candidate blocks by farthest-point
// sampling, keeping clear of the `fixed` boxes.
function spread (cands, count, fixed, ax, clear) {
    if (!cands.length || !count) return []
    const minD = new Float64Array(cands.length).fill(Infinity)
    for (const f of fixed) cands.forEach((c, i) => (minD[i] = Math.min(minD[i], gapTo(c, f, ax))))
    const points = []
    for (let n = 0; n < count; n++) {
        let best = 0
        let bestValue = -Infinity
        cands.forEach((c, i) => {
            const v = placeValue(minD[i], c, clear)
            if (v > bestValue) {
                bestValue = v
                best = i
            }
        })
        const p = { x: cands[best][0], y: cands[best][1], c: cands[best], cands }
        points.push(p)
        cands.forEach((c, i) => (minD[i] = Math.min(minD[i], gapTo(c, p, ax))))
    }
    return points
}

// Every marker in turn steps to whichever block of its own region nearby
// gives it the most room from all the others, until nobody moves. A marker
// that overlaps something looks through its whole region; one that already
// has all the room it can use stays put.
function settle (points, fixed, ax, clear, w) {
    // Each region's candidate blocks by position, for looking around a marker.
    const lookups = new Map()
    for (const p of points) {
        if (lookups.has(p.cands)) continue
        const at = new Map()
        for (const c of p.cands) at.set(c[1] * w + c[0], c)
        lookups.set(p.cands, at)
    }
    const spaceAt = (c, near, self) => {
        let space = Infinity
        for (const q of near) if (q !== self) space = Math.min(space, gapTo(c, q, ax))
        return space
    }
    const enough = clear * 1.4
    const reachY = Math.ceil(clear * 0.8)
    const reachX = Math.ceil(reachY / ax)
    const all = fixed.concat(points)
    for (let pass = 0; pass < 24; pass++) {
        let moved = false
        for (const p of points) {
            const space = spaceAt(p.c, all, p)
            if (space >= enough && p.c[2] >= 6) continue
            const overlapping = space < clear
            let looks
            let near = all
            if (overlapping) {
                looks = p.cands
            } else {
                near = all.filter(q => q !== p && gapTo([p.x, p.y], q, ax) < reachY + enough)
                looks = []
                const at = lookups.get(p.cands)
                for (let y = p.y - reachY; y <= p.y + reachY; y += 2) {
                    for (let x = p.x - reachX; x <= p.x + reachX; x += 2) {
                        const c = at.get(y * w + x)
                        if (c) looks.push(c)
                    }
                }
            }
            let best = p.c
            let bestValue = placeValue(space, p.c, clear) + 0.01
            for (const c of looks) {
                const v = placeValue(spaceAt(c, near, p), c, clear)
                if (v > bestValue) {
                    bestValue = v
                    best = c
                }
            }
            if (best !== p.c) {
                p.c = best
                p.x = best[0]
                p.y = best[1]
                moved = true
            }
        }
        if (!moved) break
    }
}

// A nearest-neighbour walk from the west, so consecutive challenges (and the
// links of a chain) sit next to each other.
function walk (points, ax) {
    const order = []
    const left = points.slice()
    if (!left.length) return order
    let at = left.reduce((a, b) => (b.x < a.x || (b.x === a.x && b.y < a.y) ? b : a))
    while (left.length) {
        left.splice(left.indexOf(at), 1)
        order.push({ x: at.x, y: at.y })
        if (!left.length) break
        const from = at
        at = left.reduce((a, b) => (gapTo([b.x, b.y], from, ax) < gapTo([a.x, a.y], from, ax) ? b : a))
    }
    return order
}

// Builds the world. `regions` is a list of { name, count, label: { w, h } }
// (the size of the region's name in blocks); `marker` is the size of a
// challenge marker in blocks. Returns { w, h, image, kind, region, level,
// biomes, regions } where every region has its biome, where its name goes and
// one slot per challenge.
export function generateWorld ({ w, h, regions: input, marker = { w: 24, h: 8 }, seed = 2027 }) {
    const n = w * h
    const rng = new XorShift128(seed)
    const kind = new Uint8Array(n)
    const elev = new Float32Array(n)
    const depth = new Int16Array(n)

    // An island that fills most of the map, with the Far Lands past the
    // ocean on the east edge.
    const farWidth = Math.max(5, Math.round(w * 0.045))
    for (let y = 0; y < h; y++) {
        const farStart = w - farWidth - Math.round(fbm(0, y / 6, seed + 3, 2) * 4)
        for (let x = 0; x < w; x++) {
            const i = y * w + x
            const nx = ((x + 0.5) / (w - farWidth)) * 2 - 1
            const ny = ((y + 0.5) / h) * 2 - 1
            const edge = Math.pow(Math.abs(nx) ** 4 + Math.abs(ny) ** 4, 0.25)
            const coast = edge + (fbm(x / 18, y / 18, seed, 4) - 0.5) * 0.42
            elev[i] = fbm(x / 22, y / 22, seed + 11, 3)
            if (x >= farStart) kind[i] = FAR_LANDS
            else if (coast > 0.84) {
                kind[i] = OCEAN
                depth[i] = Math.max(1, Math.min(14, Math.round((coast - 0.84) * 70) + 1))
            }
        }
    }

    // One continent: islands sink, lakes stay.
    const islands = components(w, h, i => kind[i] === LAND)
    for (const island of islands.slice(1)) {
        for (const i of island) {
            kind[i] = OCEAN
            depth[i] = 1
        }
    }

    // A chunk along the coast that never loaded.
    const missing = []
    const chunksX = Math.ceil(w / CHUNK)
    const chunksY = Math.ceil(h / CHUNK)
    const coastal = []
    for (let cy = 0; cy < chunksY; cy++) {
        for (let cx = 0; cx < chunksX; cx++) {
            let landCount = 0
            let all = 0
            for (let y = cy * CHUNK; y < Math.min(h, (cy + 1) * CHUNK); y++) {
                for (let x = cx * CHUNK; x < Math.min(w, (cx + 1) * CHUNK); x++) {
                    all++
                    if (kind[y * w + x] === LAND) landCount++
                }
            }
            if (landCount > all * 0.15 && landCount < all * 0.55) coastal.push([cx, cy])
        }
    }
    for (let m = Math.min(coastal.length, 1); m > 0; m--) {
        const [cx, cy] = coastal.splice(rng.int(coastal.length), 1)[0]
        missing.push([cx, cy])
        for (let y = cy * CHUNK; y < Math.min(h, (cy + 1) * CHUNK); y++) {
            for (let x = cx * CHUNK; x < Math.min(w, (cx + 1) * CHUNK); x++) kind[y * w + x] = MISSING
        }
    }

    // Regions get land in proportion to their challenges, plus room for the name.
    const biomeNames = biomesFor(input.map(r => r.name))
    const region = assignRegions(w, h, kind, input.map(r => r.count + 2), seed, rng)

    // Rivers between neighbouring regions.
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x
            if (kind[i] !== LAND || region[i] < 0) continue
            const right = x < w - 1 && kind[i + 1] === LAND && region[i + 1] >= 0 && region[i + 1] !== region[i]
            const down = y < h - 1 && kind[i + w] === LAND && region[i + w] >= 0 && region[i + w] !== region[i]
            if (right) {
                kind[i] = RIVER
                kind[i + 1] = RIVER
            }
            if (down) {
                kind[i] = RIVER
                kind[i + w] = RIVER
            }
        }
    }
    for (let i = 0; i < n; i++) if (kind[i] === RIVER) depth[i] = 1 + (hash(i, 0, seed + 5) < 0.3 ? 1 : 0)

    // Paint.
    const image = createBitmap(w, h)
    const colors = new Array(n)
    const level = new Int16Array(n)
    const block = {
        x: 0,
        y: 0,
        level: 0,
        e: 0,
        n2: 0,
        n3: 0,
        r: 0,
        color: GRASS,
        lift: 0,
        set (color, lift) {
            this.color = color
            this.lift = lift
        },
    }
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x
            const r = region[i]
            if (kind[i] === OCEAN || kind[i] === RIVER || kind[i] === MISSING) {
                colors[i] = WATER
                level[i] = SEA
                continue
            }
            if (kind[i] === FAR_LANDS) {
                // Streaks of whatever terrain, stretched sideways.
                const run = 3 + Math.floor(hash(y, 1, seed + 9) * 14)
                const s = hash(Math.floor((x + hash(y, 2, seed + 9) * 30) / run), y, seed + 9)
                colors[i] = [STONE, DIRT, GRASS, SAND, CLAY, WATER, WOOL, STONE][(s * 8) | 0]
                level[i] = 64 + Math.floor(hash(x, y, seed + 10) * 3) * 4
                continue
            }
            const biome = BIOMES[biomeNames[Math.max(0, r)]] || BIOMES.plains
            block.x = x
            block.y = y
            block.e = elev[i]
            block.n2 = fbm(x / 9, y / 9, seed + 17 + r * 7, 3)
            block.n3 = fbm(x / 7, y / 7, seed + 23 + r * 7, 2)
            block.r = hash(x, y, seed + 29)
            const base = SEA + 2 + Math.floor(block.e * 30 * (biome.relief || 1))
            block.level = base
            biome.paint(block)
            colors[i] = block.color
            if (block.lift < 0) {
                level[i] = SEA
                depth[i] = -block.lift
            } else {
                level[i] = base + block.lift
                depth[i] = 0
            }
        }
    }

    // Villages, temples and huge mushrooms, stamped onto their regions.
    const regionBlocks = input.map(() => [])
    for (let i = 0; i < n; i++) if (kind[i] === LAND && region[i] >= 0) regionBlocks[region[i]].push(i)
    const inRegion = (x, y, r) => x >= 0 && y >= 0 && x < w && y < h && kind[y * w + x] === LAND && region[y * w + x] === r
    const put = (x, y, r, color, lift) => {
        if (!inRegion(x, y, r)) return
        const i = y * w + x
        colors[i] = color
        level[i] = SEA + 2 + Math.floor(elev[i] * 30) + lift
        depth[i] = 0
    }
    biomeNames.forEach((name, r) => {
        const biome = BIOMES[name]
        const blocks = regionBlocks[r]
        if (!blocks.length) return
        const spot = () => blocks[rng.int(blocks.length)]
        if (biome.village) {
            for (let v = 0; v < 1 + (blocks.length > 900 ? 1 : 0); v++) {
                const c = spot()
                const vx = c % w
                const vy = (c / w) | 0
                for (let d = -7; d <= 7; d++) {
                    put(vx + d, vy, r, DIRT, 0)
                    put(vx, vy + d, r, DIRT, 0)
                }
                for (let k = 0; k < 6; k++) {
                    const along = rng.int(2)
                    const t = (rng.int(5) + 2) * (rng.float() < 0.5 ? -1 : 1)
                    const side = rng.float() < 0.5 ? -3 : 2
                    const hx = along ? vx + t : vx + side
                    const hy = along ? vy + side : vy + t
                    for (let yy = 0; yy < 2; yy++) for (let xx = 0; xx < 2; xx++) put(hx + xx, hy + yy, r, k % 3 ? WOOD : STONE, 3)
                }
                put(vx, vy, r, WATER, 0)
            }
        }
        if (biome.temple) {
            const c = spot()
            const tx = c % w
            const ty = (c / w) | 0
            for (let yy = -2; yy <= 2; yy++) {
                for (let xx = -2; xx <= 2; xx++) {
                    const ring = Math.max(Math.abs(xx), Math.abs(yy))
                    put(tx + xx, ty + yy, r, ring === 1 ? TERRACOTTA_ORANGE : ring === 0 ? LIGHT_BLUE : TERRACOTTA_WHITE, 6 - ring * 2)
                }
            }
        }
        if (biome.mushrooms) {
            for (let m = 0; m < Math.max(2, blocks.length / 160); m++) {
                const c = spot()
                const color = rng.float() < 0.5 ? RED : DIRT
                const rad = 1 + rng.int(2)
                for (let yy = -rad; yy <= rad; yy++) {
                    for (let xx = -rad; xx <= rad; xx++) {
                        if (xx * xx + yy * yy > rad * rad + 1) continue
                        put((c % w) + xx, ((c / w) | 0) + yy, r, color, 6 - Math.abs(yy))
                    }
                }
            }
        }
    })

    // Shade like a map item and tone it down.
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x
            let k
            if (depth[i] > 0) {
                const d = depth[i] * 0.1 + ((x + y) & 1) * 0.2
                k = d < 0.5 ? HIGH : d > 0.9 ? LOW : NORMAL
            } else {
                const north = y > 0 ? level[i - w] : level[i]
                k = level[i] > north ? HIGH : level[i] < north ? LOW : NORMAL
            }
            tone(colors[i], k, image.d, i * 4)
        }
    }

    // Damage. Missing chunks first, so the rest can tear through them.
    for (const [cx, cy] of missing) missingTexture(image, null, cx * CHUNK, cy * CHUNK, CHUNK, CHUNK, 2)
    for (let cy = 0; cy < chunksY; cy++) {
        for (let cx = 0; cx < chunksX; cx++) {
            if (rng.int(100) < 7) crunch(image, cx * CHUNK, cy * CHUNK, rng)
        }
    }
    for (let t = Math.ceil(h / 40); t > 0; t--) {
        shiftRows(image, rng.int(h), 1 + rng.int(2), rng.int(13) - 6, true)
    }
    for (let s = 0; s < 3; s++) {
        const y0 = rng.int(h)
        sortColumns(image, w - farWidth - 2 + rng.int(farWidth), 1 + rng.int(3), y0, y0 + 10 + rng.int(h / 2))
    }

    // Where each region's name and challenges go.
    const regionDist = distanceInside(w, h, i => kind[i] === LAND && region[i] >= 0 && region[i] === region[i - 1] &&
        region[i] === region[i + 1] && region[i] === region[i - w] && region[i] === region[i + w])
    // Names first, on the most open spot their box fits in.
    const ax = marker.h / marker.w
    const labels = input.map((spec, r) => {
        const lw = spec.label ? spec.label.w : 8
        const lh = spec.label ? spec.label.h : 3
        const cands = regionBlocks[r].filter(i => kind[i] === LAND).map(i => [i % w, (i / w) | 0, regionDist[i]])
        const fits = c => [[-lw / 2, -lh / 2], [lw / 2, -lh / 2], [-lw / 2, lh / 2], [lw / 2, lh / 2], [0, -lh / 2], [0, lh / 2]]
            .every(([dx, dy]) => inRegion(Math.round(c[0] + dx), Math.round(c[1] + dy), r))
        const byRoom = cands.sort((a, b) => b[2] - a[2])
        const at = byRoom.find(fits) || byRoom[0] || [0, 0, 0]
        return { box: { x: at[0], y: at[1], hw: lw / 2, hh: lh / 2 }, cands: byRoom }
    })
    const names = labels.map(l => l.box)

    // Then the markers, each region keeping clear of the ones placed before it.
    const placed = []
    const perRegion = input.map((spec, r) => {
        const cands = labels[r].cands
        const margin = Math.min(3, Math.max(1, (cands[0] || [0, 0, 1])[2] - 1))
        const usable = cands.filter(c => c[2] >= margin)
        const points = spread(usable.length ? usable : cands, spec.count, names.concat(placed), ax, marker.h)
        placed.push(...points)
        return points
    })
    settle(placed, names, ax, marker.h, w)

    const regions = input.map((spec, r) => ({
        name: spec.name,
        biome: BIOMES[biomeNames[r]].id,
        label: { x: names[r].x, y: names[r].y },
        slots: walk(perRegion[r], ax),
    }))

    return { w, h, image, kind, region, level, biomes: biomeNames.map(b => BIOMES[b].id), regions, missing }
}

// What F3 would say about a block.
export function describeBlock (world, x, y) {
    const i = y * world.w + x
    const kind = world.kind[i]
    const r = world.region[i]
    let biome = r >= 0 ? world.biomes[r] : 'minecraft:ocean'
    if (kind === RIVER) biome = 'minecraft:river'
    if (kind === FAR_LANDS) biome = 'minecraft:the_far_lands'
    if (kind === MISSING) biome = 'minecraft:missingno'
    return {
        x: x - (world.w >> 1),
        y: kind === MISSING ? -64 : world.level[i],
        z: y - (world.h >> 1),
        biome,
        region: kind === LAND && r >= 0 ? world.regions[r].name : null,
    }
}
